import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import useAccountQuery from 'api/queries/useAccountQuery';
import { BACK_IN_STOCK_WAITLIST_ITEMS_QUERY_KEY } from 'api/queries/useBackInStockWaitlistItemsQuery';
import { SELECTIONS_QUERY_KEY } from 'api/queries/useSelectionsQuery';
import useUnviewedBackInStockWaitlistFulfillmentsQuery from 'api/queries/useUnviewedBackInStockWaitlistFulfillmentsQuery';
import { FEATURE_FLAG_NAMES, useAmplitudeFeatureFlag } from 'hooks/useFeatureFlag';

import useMarkBackInStockWaitlistFulfillmentsViewed from './useMarkBackInStockWaitlistFulfillmentsViewed';

/**
 * SPA-session dismissed fulfillment request IDs.
 * Shared across remounts of `ShopLayout` / `ProductDetailPageLayout` (sibling `/shop` routes)
 * so closing the modal on one surface does not reopen it on the other while mark-viewed is in flight.
 */
const sessionDismissedRequestIDs = new Set();

/** Clears session dismissals. Used by unit tests. */
export function clearSessionDismissedBackInStockWaitlistFulfillmentIDs() {
    sessionDismissedRequestIDs.clear();
}

/**
 * Drives open/close state for {@link BackInStockWaitlistSuccessModal}.
 * Fetches unviewed back-in-stock waitlist fulfillments and opens the modal when fulfilled items exist.
 *
 * @returns {object} Modal wiring for `ShopLayout`.
 * @returns {boolean} return.backInStockWaitlistSuccessModalOpen - Whether the success modal should be open.
 * @returns {() => void} return.onCloseBackInStockWaitlistSuccessModal - Marks current fulfillments as viewed and closes the modal; later unviewed fulfillments can reopen it.
 * @returns {import('features/shop/ProductCardV2/types').ProductData[]} return.products - Product data for fulfilled items.
 */
function useBackInStockWaitlistSuccessModal() {
    const queryClient = useQueryClient();
    const { data: account } = useAccountQuery();
    const chargeID = account?.nextOrder?.chargeID;

    const { isAssignedVariant: isBackInStockWaitlistEnabled } = useAmplitudeFeatureFlag({
        flagName: FEATURE_FLAG_NAMES.BACK_IN_STOCK_WAITLIST,
        variant: 'on',
    });

    const {
        data: fulfillmentsData = {},
        isError,
        isLoading,
    } = useUnviewedBackInStockWaitlistFulfillmentsQuery({
        queryProps: { enabled: !!isBackInStockWaitlistEnabled },
    });

    const items = fulfillmentsData?.items;

    // Track dismissed fulfillment request IDs so closing the modal only suppresses those
    // items — later unviewed fulfillments in the same SPA session can still reopen it.
    // Seeded from module-level session set so sibling shop/PDP layouts share dismissals.
    const [dismissedRequestIDs, setDismissedRequestIDs] = useState(() => new Set(sessionDismissedRequestIDs));

    const displayProducts = useMemo(
        () =>
            (items ?? []).filter(
                (item) =>
                    item?.id != null &&
                    item?.backInStockWaitlistRequestID != null &&
                    !dismissedRequestIDs.has(item.backInStockWaitlistRequestID)
            ),
        [dismissedRequestIDs, items]
    );

    const hasSyncedQueriesRef = useRef(false);
    const hasSyncedQueriesOnErrorRef = useRef(false);
    const requestIDsToMarkViewedRef = useRef([]);
    const { mutate: markFulfillmentsViewed } = useMarkBackInStockWaitlistFulfillmentsViewed();

    const shouldShowModal = useMemo(() => {
        if (!isBackInStockWaitlistEnabled) return false;
        if (isLoading) return false;
        return displayProducts.length > 0;
    }, [displayProducts.length, isBackInStockWaitlistEnabled, isLoading]);

    useEffect(() => {
        if (!shouldShowModal) {
            requestIDsToMarkViewedRef.current = [];
            return;
        }

        requestIDsToMarkViewedRef.current = displayProducts
            .map((item) => item.backInStockWaitlistRequestID)
            .filter((id) => id != null);
    }, [displayProducts, shouldShowModal]);

    useEffect(() => {
        if (!shouldShowModal) {
            hasSyncedQueriesRef.current = false;
            return;
        }

        if (!chargeID || hasSyncedQueriesRef.current) {
            return;
        }

        hasSyncedQueriesRef.current = true;
        // Refresh cart and waitlist membership so cards stop showing manage-waitlist
        // for items that were auto-added / fulfilled.
        queryClient.invalidateQueries({ queryKey: [SELECTIONS_QUERY_KEY, chargeID] });
        queryClient.invalidateQueries({ queryKey: [BACK_IN_STOCK_WAITLIST_ITEMS_QUERY_KEY, chargeID] });
    }, [chargeID, queryClient, shouldShowModal]);

    // If fulfillments fail to load, still refresh selections and waitlist so UI stays current
    // (fulfilled waitlist items may already have been added server-side).
    useEffect(() => {
        if (!isBackInStockWaitlistEnabled || !isError || !chargeID || hasSyncedQueriesOnErrorRef.current) {
            return;
        }

        hasSyncedQueriesOnErrorRef.current = true;
        queryClient.invalidateQueries({ queryKey: [SELECTIONS_QUERY_KEY, chargeID] });
        queryClient.invalidateQueries({ queryKey: [BACK_IN_STOCK_WAITLIST_ITEMS_QUERY_KEY, chargeID] });
    }, [chargeID, isBackInStockWaitlistEnabled, isError, queryClient]);

    const onCloseBackInStockWaitlistSuccessModal = useCallback(() => {
        const requestIDs = requestIDsToMarkViewedRef.current;

        if (requestIDs.length > 0 && chargeID) {
            markFulfillmentsViewed({ chargeID, requestIDs });
        }

        requestIDs.forEach((id) => sessionDismissedRequestIDs.add(id));
        setDismissedRequestIDs(new Set(sessionDismissedRequestIDs));
    }, [chargeID, markFulfillmentsViewed]);

    return {
        backInStockWaitlistSuccessModalOpen: shouldShowModal,
        onCloseBackInStockWaitlistSuccessModal,
        products: displayProducts,
    };
}

export default useBackInStockWaitlistSuccessModal;
